import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import GroupIcon from '@mui/icons-material/Group';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { useLiveQuizRoom } from '../../../hooks/useLiveQuizRoom';
import { abortRoom } from '../../../services/liveQuizService';
import LiveScoreboard from '../../../components/quiz/live/LiveScoreboard';
import { LiveRoomStatus } from '../../../types/liveQuiz';

/**
 * Admin monitor for a running live quiz.
 *
 * Read-only by design apart from the abort control. The quiz advances on its own
 * schedule — there is no "next question" button, because the room is driven by
 * timestamps rather than by a host, and a quiz must be able to run unattended.
 */
const LiveQuizControlPage: React.FC = () => {
  const { roomId: roomIdParam } = useParams<{ roomId: string }>();
  const roomId = roomIdParam ? Number(roomIdParam) : null;
  const theme = useTheme();

  const { state, mode, error, remainingMs, refresh } = useLiveQuizRoom(roomId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [aborting, setAborting] = useState(false);

  const handleAbort = async () => {
    if (roomId === null) return;
    setAborting(true);
    try {
      await abortRoom(roomId);
      await refresh();
    } finally {
      setAborting(false);
      setConfirmOpen(false);
    }
  };

  if (roomId === null) {
    return <Alert severity="error">No room specified.</Alert>;
  }

  if (!state) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isRunning = !['finished', 'abandoned'].includes(state.status);
  const secondsLeft = remainingMs === null ? null : Math.ceil(remainingMs / 1000);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 4,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
            theme.palette.secondary.main,
            0.1
          )} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}
      >
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          Live Quiz Monitor
        </Typography>
        <Typography variant="h6" color="text.secondary" fontWeight={400}>
          This quiz runs on its own schedule — no action needed unless something goes wrong.
        </Typography>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <StatCard
          label="Phase"
          value={phaseLabel(state.status)}
          detail={secondsLeft !== null ? `${secondsLeft}s remaining` : undefined}
          color={theme.palette.primary.main}
        />
        <StatCard
          label="Question"
          value={`${Math.max(state.currentQuestionIndex + 1, 0)} / ${state.totalQuestions}`}
          color={theme.palette.secondary.main}
        />
        <StatCard
          label="Participants"
          value={String(state.participantCount)}
          icon={<GroupIcon />}
          color={theme.palette.warning.main}
        />
        <StatCard
          label="Connection"
          value={mode === 'stream' ? 'Streaming' : mode === 'polling' ? 'Polling' : 'Connecting'}
          icon={<WifiTetheringIcon />}
          color={mode === 'stream' ? theme.palette.success.main : theme.palette.warning.main}
        />
      </Grid>

      {state.scoreboard && state.scoreboard.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <LiveScoreboard entries={state.scoreboard} title="Current standings" />
        </Box>
      )}

      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Stop this quiz
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {isRunning
              ? 'Ends the quiz immediately for everyone. Answers already given are kept, but no ratings will be applied. This cannot be undone.'
              : `This quiz has already ${state.status === 'finished' ? 'finished' : 'been stopped'}.`}
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<StopCircleIcon />}
            disabled={!isRunning || aborting}
            onClick={() => setConfirmOpen(true)}
            sx={{ borderRadius: 2, borderWidth: 2, '&:hover': { borderWidth: 2 } }}
          >
            Stop quiz
          </Button>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Stop this quiz for everyone?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            All {state.participantCount} students will be dropped out of the quiz immediately. No
            ratings will be applied. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Keep running</Button>
          <Button onClick={handleAbort} color="error" variant="contained" disabled={aborting}>
            Stop quiz
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  detail?: string;
  icon?: React.ReactNode;
  color: string;
}> = ({ label, value, detail, icon, color }) => (
  <Grid item xs={6} md={3}>
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
        border: `1px solid ${alpha(color, 0.2)}`
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color }}>
          {icon}
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
        </Box>
        <Typography variant="h5" fontWeight={700} sx={{ color, lineHeight: 1 }}>
          {value}
        </Typography>
        {detail && (
          <Typography variant="caption" color="text.disabled">
            {detail}
          </Typography>
        )}
      </CardContent>
    </Card>
  </Grid>
);

function phaseLabel(status: LiveRoomStatus): string {
  switch (status) {
    case 'scheduled':
      return 'Not open yet';
    case 'lobby':
      return 'Lobby';
    case 'question':
      return 'Question';
    case 'reveal':
      return 'Answer';
    case 'scoreboard':
      return 'Scoreboard';
    case 'finished':
      return 'Finished';
    case 'abandoned':
      return 'Stopped';
    default:
      return status;
  }
}

export default LiveQuizControlPage;
